import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, User as UserIcon, ShieldCheck, FileText, Trash2, LogOut, ChevronRight, Sparkles, Globe, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { http } from "../lib/api";
import { useT } from "../lib/i18n";

export default function Settings() {
  const { user, logout, login } = useAuth();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [entitlement, setEntitlement] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/iap/entitlement");
        setEntitlement(data);
      } catch {}
    })();
  }, []);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await http.delete("/account");
      toast.success("Akaun anda telah dipadam.");
      // Force logout state
      window.location.href = "/";
    } catch (e) {
      toast.error("Gagal padam akaun. Sila cuba lagi.");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E8E8ED]">
        <div className="max-w-2xl mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-[#F5F5F7]" data-testid="settings-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#0A2540]" />
          </Link>
          <div className="font-semibold text-[#0A2540]">{t("settings.title")}</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-6 space-y-5">
        {/* Language switcher */}
        <section className="bg-white rounded-3xl p-4 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E8E8ED] flex items-center gap-3" data-testid="language-section">
          <Globe className="w-5 h-5 text-[#4FB3E8]" />
          <div className="flex-1">
            <div className="text-sm font-medium text-[#1D1D1F]">{t("settings.language")}</div>
          </div>
          <div className="inline-flex rounded-full bg-[#F5F5F7] p-1">
            <button
              data-testid="lang-ms-btn"
              onClick={() => setLang("ms")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${lang === "ms" ? "bg-white text-[#0A2540] shadow" : "text-gray-500"}`}
            >
              BM
            </button>
            <button
              data-testid="lang-en-btn"
              onClick={() => setLang("en")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${lang === "en" ? "bg-white text-[#0A2540] shadow" : "text-gray-500"}`}
            >
              EN
            </button>
          </div>
        </section>

        {/* Profile */}
        <section className="bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E8E8ED]">
          {user ? (
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#F5F5F7] flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0A2540]">{user.name || user.email}</div>
                <div className="text-xs text-gray-500 truncate">{user.email}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">via {user.provider || "google"}</div>
              </div>
              {entitlement?.entitled && (
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#0A2540] text-[#D4AF37] font-medium">Full Access</span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-[#0A2540]">{t("settings.not_logged_in")}</div>
                <div className="text-xs text-gray-500">{t("settings.not_logged_in_desc")}</div>
              </div>
              <button onClick={login} data-testid="settings-login-btn" className="chip bg-[#0A2540] text-white px-4 py-2 rounded-full text-sm">{t("topbar.login")}</button>
            </div>
          )}
        </section>

        {/* Paywall entry */}
        {(!entitlement?.entitled) && (
          <Link to="/paywall" data-testid="paywall-link" className="block bg-gradient-to-br from-[#0A2540] to-[#15374E] text-white rounded-3xl p-5 shadow-[0_12px_48px_rgba(10,37,64,0.15)] relative overflow-hidden">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#D4AF37]/20 blur-2xl" />
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#0A2540]" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.15em] text-[#4FB3E8]">Premium</div>
                <div className="font-semibold text-lg">TAi-P Full Access</div>
                <div className="text-xs text-white/70">Bayaran satu kali · Unlock semua ciri selamanya</div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60" />
            </div>
          </Link>
        )}

        {/* Privacy & Account */}
        <section className="bg-white rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E8E8ED] overflow-hidden">
          <SectionHeader>{t("settings.privacy_account")}</SectionHeader>
          <Row to="/privacy" icon={<ShieldCheck className="w-5 h-5 text-[#4FB3E8]" />} label={t("settings.privacy")} testid="row-privacy" />
          <Row to="/terms" icon={<FileText className="w-5 h-5 text-[#4FB3E8]" />} label={t("settings.terms")} testid="row-terms" />
          <Row to="/disclaimer" icon={<FileText className="w-5 h-5 text-[#4FB3E8]" />} label={t("settings.disclaimer")} testid="row-disclaimer" />
          <Row to="/support" icon={<LifeBuoy className="w-5 h-5 text-[#4FB3E8]" />} label={t("settings.support")} testid="row-support" />
          <Row to="/support/purchase-restore" icon={<LifeBuoy className="w-5 h-5 text-[#4FB3E8]" />} label={lang === "en" ? "Purchase Restoration" : "Pemulihan Pembelian"} testid="row-purchase-restore" />
          {user && (
            <button
              data-testid="delete-account-btn"
              onClick={() => setConfirmDelete(true)}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#FFF5F5] text-left transition-colors border-t border-[#F5F5F7]"
            >
              <Trash2 className="w-5 h-5 text-red-500" />
              <span className="flex-1 text-red-600 font-medium">{t("settings.delete_account")}</span>
              <ChevronRight className="w-4 h-4 text-red-300" />
            </button>
          )}
          {user && (
            <button
              data-testid="settings-logout-btn"
              onClick={async () => { await logout(); toast.success(t("toast.logout_ok")); navigate("/"); }}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#F5F5F7] text-left transition-colors border-t border-[#F5F5F7]"
            >
              <LogOut className="w-5 h-5 text-gray-500" />
              <span className="flex-1 text-[#1D1D1F]">{t("menu.logout")}</span>
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </button>
          )}
        </section>

        <p className="text-[11px] text-gray-400 text-center pt-4">TAi-P · Plan Smarter. Travel Better.</p>
      </main>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !deleting && setConfirmDelete(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-modal">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-center text-[#0A2540]">{t("settings.confirm_delete_title")}</h3>
            <p className="text-sm text-gray-500 text-center mt-2">
              {t("settings.confirm_delete_desc")}
            </p>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setConfirmDelete(false)} disabled={deleting} className="flex-1 py-2.5 rounded-full bg-[#F5F5F7] text-[#1D1D1F] text-sm font-medium">{t("common.cancel")}</button>
              <button data-testid="confirm-delete-btn" onClick={doDelete} disabled={deleting} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium disabled:opacity-60">
                {deleting ? t("common.deleting") : t("common.confirm_delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }) {
  return <div className="px-5 pt-4 pb-2 text-[11px] uppercase tracking-[0.15em] text-gray-400">{children}</div>;
}

function Row({ to, icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 px-5 py-4 hover:bg-[#F5F5F7] transition-colors border-t border-[#F5F5F7]">
      {icon}
      <span className="flex-1 text-[#1D1D1F]">{label}</span>
      <ChevronRight className="w-4 h-4 text-gray-300" />
    </Link>
  );
}
